/**
 * CampaignRemapper
 *
 * Bridges raw Firestore JSON → CampaignDto → CampaignEntity.
 * Flutter source: lib/data/models/campaign.dart (+ campaign.g.dart),
 * lib/data/models/campaign_condition.dart (+ campaign_condition.g.dart).
 *
 * `fromJson` applies constructor defaults defensively (booleans `?? false`,
 * numerics `?? 0`, optionals stay undefined when absent), and maps the nested
 * `conditions` list via the condition shape. `fromDto` resolves `campaignType`
 * from the raw numeric `type` via index, mirroring Flutter's
 * `CampaignType.values[campaign.type]`
 * (campaign_ranking_bottom_sheet.dart:50).
 */
import type { CampaignConditionDto } from '@/data/dtos/campaign/campaignConditionDto';
import type { CampaignDto } from '@/data/dtos/campaign/campaignDto';
import type { JsonObject } from '@/data/types/json';
import type { CampaignConditionEntity } from '@/domain/entities/campaign/campaignConditionEntity';
import type { CampaignEntity } from '@/domain/entities/campaign/campaignEntity';
import type { CampaignType } from '@/domain/enums/campaign/campaignType';

export class CampaignRemapper {
  private static conditionFromJson(data: JsonObject): CampaignConditionDto {
    return {
      description: (data['description'] as string | undefined) ?? '',
      minValue: data['minValue'] as number | undefined,
    };
  }

  static fromJson(data: JsonObject): CampaignDto {
    const rawConditions = data['conditions'] as JsonObject[] | undefined;
    return {
      id: (data['id'] as string | undefined) ?? '',
      active: (data['active'] as boolean | undefined) ?? false,
      startDate: (data['startDate'] as number | undefined) ?? 0,
      endDate: (data['endDate'] as number | undefined) ?? 0,
      title: (data['title'] as string | undefined) ?? '',
      subtitle: data['subtitle'] as string | undefined,
      description: data['description'] as string | undefined,
      policy: data['policy'] as string | undefined,
      conditions: rawConditions?.map(CampaignRemapper.conditionFromJson),
      type: (data['type'] as number | undefined) ?? 0,
      rankingFetchLimit: (data['rankingFetchLimit'] as number | undefined) ?? 0,
      rewards: (data['rewards'] as number | undefined) ?? 0,
      image: data['image'] as string | undefined,
    };
  }

  static fromJsonList(dataList: JsonObject[]): CampaignDto[] {
    return dataList.map(CampaignRemapper.fromJson);
  }

  private static conditionFromDto(
    dto: CampaignConditionDto,
  ): CampaignConditionEntity {
    return {
      description: dto.description,
      minValue: dto.minValue,
    };
  }

  static fromDto(dto: CampaignDto): CampaignEntity {
    return {
      id: dto.id,
      active: dto.active,
      startDate: dto.startDate,
      endDate: dto.endDate,
      title: dto.title,
      subtitle: dto.subtitle,
      description: dto.description,
      policy: dto.policy,
      conditions: dto.conditions?.map(CampaignRemapper.conditionFromDto),
      type: dto.type,
      // `CampaignType` is a numeric enum (0/1/2), so the raw `type` index IS the
      // enum value, exactly like Dart's `CampaignType.values[campaign.type]`.
      campaignType: dto.type as CampaignType,
      rankingFetchLimit: dto.rankingFetchLimit,
      rewards: dto.rewards,
      image: dto.image,
    };
  }

  static fromDtoList(dtoList: CampaignDto[]): CampaignEntity[] {
    return dtoList.map(CampaignRemapper.fromDto);
  }
}
